// ============================================================================
// OBT — "Önizle ve Gönder" (Model A: doldur → önizle → hepsini gönder)
// ============================================================================
// Formen tüm parkları TASLAK doldurur, buradan verdiği cevapları bir arada
// gözden geçirir; hata görürse taslağı "Düzelt" ile açıp düzeltir (kilit YOK),
// memnunsa "Hepsini Gönder" ile tek işlemde gönderir. Eksik taslaklar backend'de
// atlanır ve gerekçesiyle raporlanır. Gönderim sonrası cevaplar kilitlenir.
//
// Kart tasarımı KULLANICI-DOSTU: her SORU ayrı satırda etiketiyle; cevap
// RENK-KODLU ve belirgin (Evet=yeşil, Hayır=kırmızı, seçenek=mavi, metin=alıntı);
// CEVAPLANMAMIŞ/eksik fotoğraf KIRMIZI "Eksik" ile işaretlenir → hata görünür.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Camera,
  ImageOff,
  Loader2,
  Pencil,
  Send,
  X,
} from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState } from '../ObtStates';
import { fetchMyResponseDetail, submitAllDrafts } from './responseApi';
import PhotoThumb from './PhotoThumb';
import { normalizeObtError } from '../obtErrors';

const isDraft = (r) => r?.status === 'draft';

/** Cevap değeri boş mu (soru yanıtlanmamış)? */
function isEmptyAnswer(a) {
  if (!a) return true;
  if (a.answer_boolean === true || a.answer_boolean === false) return false;
  if (Array.isArray(a.option_values) && a.option_values.length > 0) return false;
  if (a.answer_text && a.answer_text.trim()) return false;
  return true;
}

/** Tek cevabı BELİRGİN, renk-kodlu göster (hata gözle yakalansın). */
function AnswerValue({ answer }) {
  if (isEmptyAnswer(answer)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-300">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        Cevaplanmadı
      </span>
    );
  }
  if (answer.answer_boolean === true) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-sm font-semibold text-green-700 ring-1 ring-inset ring-green-300">
        <Check className="h-4 w-4" aria-hidden="true" />
        Evet
      </span>
    );
  }
  if (answer.answer_boolean === false) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-300">
        <X className="h-4 w-4" aria-hidden="true" />
        Hayır
      </span>
    );
  }
  if (Array.isArray(answer.option_values) && answer.option_values.length > 0) {
    return (
      <span className="flex flex-wrap gap-1">
        {answer.option_values.map((o, i) => (
          <span
            key={`${o}-${i}`}
            className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
          >
            {o}
          </span>
        ))}
      </span>
    );
  }
  return (
    <span className="block rounded-md bg-gray-50 px-2.5 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-200">
      {answer.answer_text}
    </span>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.publicationId
 * @param {Array}  props.parks       PublicationDetail'in park listesi (response_id ile eşleşir)
 * @param {Array}  props.questions   Yayın snapshot soruları (tümünü göstermek + eksik yakalamak için)
 * @param {() => void} props.onClose
 * @param {(park:object) => void} props.onEditPark
 * @param {() => void} props.onSubmitted
 */
export default function PreviewSubmitDialog({
  open,
  publicationId,
  parks = [],
  questions = [],
  onClose,
  onEditPark,
  onSubmitted,
}) {
  const [state, setState] = useState({ loading: true, error: null, responses: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(
    (signal) => {
      if (!publicationId) return;
      setResult(null);
      setState({ loading: true, error: null, responses: [] });
      fetchMyResponseDetail(publicationId, { signal })
        .then((data) =>
          setState({
            loading: false,
            error: null,
            responses: Array.isArray(data?.responses) ? data.responses : [],
          })
        )
        .catch((e) => {
          if (signal?.aborted) return;
          setState({ loading: false, error: normalizeObtError(e), responses: [] });
        });
    },
    [publicationId]
  );

  useEffect(() => {
    if (!open || !publicationId) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [open, publicationId, load]);

  const responses = state.responses;
  const parkByResponse = useMemo(
    () =>
      new Map(
        parks.filter((p) => p?.response_id).map((p) => [String(p.response_id), p])
      ),
    [parks]
  );

  // Bir cevabın (park) eksikleri: yanıtlanmamış zorunlu soru + eksik zorunlu foto.
  const shortfalls = useCallback(
    (response) => {
      const list = [];
      const answerFor = (qid) =>
        (response.answers || []).find(
          (a) => String(a.question_id) === String(qid)
        );
      (questions || []).forEach((q) => {
        const a = answerFor(q.question_id);
        if (q.is_required && isEmptyAnswer(a)) {
          list.push(`“${q.question_text}” cevaplanmadı`);
        }
        if (q.photo_required && (a?.photo_ids?.length || 0) === 0) {
          list.push(`“${q.question_text}” için fotoğraf gerekli`);
        }
      });
      return list;
    },
    [questions]
  );

  const draftCount = responses.filter(isDraft).length;
  const submittedCount = responses.filter((r) => !isDraft(r)).length;
  const incompleteDraftCount = responses.filter(
    (r) => isDraft(r) && shortfalls(r).length > 0
  ).length;

  const handleSubmitAll = async () => {
    if (!publicationId || submitting || draftCount === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await submitAllDrafts(publicationId);
      setResult(res);
      load();
      if ((res?.submitted ?? 0) > 0 && onSubmitted) onSubmitted();
    } catch (e) {
      setResult({ error: normalizeObtError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (response) => {
    const park = parkByResponse.get(String(response.response_id));
    if (park && onEditPark) {
      onClose();
      onEditPark(park);
    }
  };

  // Soru listesi yoksa (yedek) cevaplardan türet — yine de bir şey göster.
  const questionList =
    questions && questions.length > 0
      ? questions
      : []; // boşsa cevapların kendi soru metinlerini kullanırız (aşağıda)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Önizle ve Gönder</DialogTitle>
          <DialogDescription>
            Cevaplarınızı gönderMEDEN önce gözden geçirin. Hata görürseniz{' '}
            <strong>Düzelt</strong> ile taslağı düzeltebilirsiniz. Gönderim sonrası
            cevaplar kilitlenir.
          </DialogDescription>
        </DialogHeader>

        {/* Özet */}
        <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
          <span className="font-semibold">{parks.length}</span> park •{' '}
          <span className="font-semibold">{responses.length}</span> dolduruldu •{' '}
          <span className="font-semibold text-amber-700">{draftCount}</span> taslak •{' '}
          <span className="font-semibold text-green-700">{submittedCount}</span> gönderildi
          {incompleteDraftCount > 0 && (
            <span className="mt-1 flex items-center gap-1 text-xs font-medium text-red-600">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              {incompleteDraftCount} taslakta eksik var — düzeltmeden gönderilemez.
            </span>
          )}
          {parks.length > responses.length && (
            <span className="mt-0.5 block text-xs text-gray-500">
              {parks.length - responses.length} park henüz doldurulmadı (gönderilmez).
            </span>
          )}
        </div>

        {/* Toplu gönderim sonucu */}
        {result && !result.error && (
          <div
            role="status"
            className={`rounded-lg p-3 text-sm ring-1 ring-inset ${
              (result.failed || []).length > 0
                ? 'bg-amber-50 text-amber-800 ring-amber-200'
                : 'bg-green-50 text-green-800 ring-green-200'
            }`}
          >
            <p className="font-semibold">{result.submitted ?? 0} cevap gönderildi.</p>
            {(result.failed || []).length > 0 && (
              <>
                <p className="mt-1">Gönderilemeyen (eksik) parklar:</p>
                <ul className="mt-0.5 list-inside list-disc space-y-0.5 text-xs">
                  {result.failed.map((f) => (
                    <li key={f.response_id}>
                      <strong>{f.park_name || 'Park'}:</strong> {f.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
        {result?.error && (
          <p className="text-sm text-red-600" role="alert">
            {result.error.message || 'Gönderilemedi.'}
          </p>
        )}

        {state.loading && <ObtLoading label="Cevaplarınız yükleniyor…" />}
        {!state.loading && state.error && (
          <ObtErrorState error={state.error} onRetry={() => load()} />
        )}
        {!state.loading && !state.error && responses.length === 0 && (
          <ObtEmptyState
            title="Henüz cevap yok"
            description="Bir parkı 'Cevapla' ile doldurup taslak kaydedin; sonra buradan gönderin."
          />
        )}

        {!state.loading && !state.error && responses.length > 0 && (
          <ul className="space-y-3">
            {responses.map((r) => {
              const draft = isDraft(r);
              const missing = draft ? shortfalls(r) : [];
              const incomplete = missing.length > 0;
              const answerFor = (qid) =>
                (r.answers || []).find(
                  (a) => String(a.question_id) === String(qid)
                );
              // Gösterilecek soru satırları: snapshot varsa TÜMÜ (eksikler dahil),
              // yoksa yalnız cevabı olanlar.
              const rows =
                questionList.length > 0
                  ? questionList.map((q) => ({
                      key: String(q.question_id),
                      text: q.question_text,
                      required: q.is_required,
                      photoRequired: q.photo_required,
                      answer: answerFor(q.question_id),
                    }))
                  : (r.answers || []).map((a) => ({
                      key: String(a.question_id),
                      text: a.question_text || 'Soru',
                      required: false,
                      photoRequired: false,
                      answer: a,
                    }));
              return (
                <li
                  key={r.response_id}
                  className={`rounded-xl border p-3.5 ${
                    incomplete
                      ? 'border-red-200 bg-red-50/30'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Park başlığı */}
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {r.park_name || 'Park'}
                      </span>
                      {draft ? (
                        incomplete ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-300">
                            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                            Eksik
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-300">
                            Taslak — hazır
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-300">
                          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                          Gönderildi
                        </span>
                      )}
                    </div>
                    {draft && parkByResponse.has(String(r.response_id)) && (
                      <Button
                        variant={incomplete ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleEdit(r)}
                        disabled={submitting}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Düzelt
                      </Button>
                    )}
                  </div>

                  {/* Soru → cevap satırları */}
                  <div className="space-y-3">
                    {rows.map((row) => {
                      const noPhoto =
                        row.photoRequired &&
                        (row.answer?.photo_ids?.length || 0) === 0;
                      return (
                        <div key={row.key}>
                          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                            {row.text}
                            {row.required && (
                              <span className="ml-1 text-red-500">*</span>
                            )}
                          </p>
                          <AnswerValue answer={row.answer} />
                          {/* Fotoğraflar / eksik foto uyarısı */}
                          {Array.isArray(row.answer?.photo_ids) &&
                            row.answer.photo_ids.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-2">
                                {row.answer.photo_ids.map((pid) => (
                                  <PhotoThumb
                                    key={pid}
                                    responseId={r.response_id}
                                    photoId={pid}
                                  />
                                ))}
                              </div>
                            )}
                          {noPhoto && (
                            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-red-600">
                              <ImageOff className="h-3.5 w-3.5" aria-hidden="true" />
                              Fotoğraf gerekli — eklenmemiş
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Genel not */}
                  {r.response_text && (
                    <div className="mt-3 rounded-md bg-gray-50 p-2 ring-1 ring-inset ring-gray-200">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        Genel not
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-gray-800">
                        {r.response_text}
                      </p>
                    </div>
                  )}

                  {/* Diğer (soruya bağlı olmayan) fotoğraflar */}
                  {Array.isArray(r.other_photo_ids) &&
                    r.other_photo_ids.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                          <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                          Diğer fotoğraflar
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {r.other_photo_ids.map((pid) => (
                            <PhotoThumb
                              key={pid}
                              responseId={r.response_id}
                              photoId={pid}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Kapat
          </Button>
          <Button onClick={handleSubmitAll} disabled={submitting || draftCount === 0}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : draftCount > 0 ? (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {draftCount > 0 ? `Hepsini Gönder (${draftCount})` : 'Gönderilecek taslak yok'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
